/**
 * PostHog analytics loader — GDPR-compliant.
 *
 * PostHog is NEVER loaded until the visitor has explicitly granted consent
 * via the CookieConsent banner. This module keeps the loader idempotent
 * so a page navigation after consent doesn't re-inject the snippet.
 *
 * Public API:
 *   - CONSENT_KEY, CONSENT_VERSION — the localStorage key/schema
 *   - readConsent()               — {analytics: 'granted'|'denied'|null, v}
 *   - grantAnalyticsConsent()     — user clicked "Accepter"
 *   - denyAnalyticsConsent()      — user clicked "Refuser"
 *   - resetConsent()              — remove stored choice (for the "Modifier
 *                                   mes choix" link in the footer)
 *   - initPostHogIfConsented()    — call on app boot; loads PostHog only
 *                                   when consent is 'granted'
 */

export const CONSENT_KEY = "sn_cookie_consent";
export const CONSENT_VERSION = 1;

const POSTHOG_KEY = "phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs";
const POSTHOG_HOST = "https://us.i.posthog.com";

export function readConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return { analytics: null, v: null };
    const parsed = JSON.parse(raw);
    if (parsed?.v !== CONSENT_VERSION) return { analytics: null, v: null };
    return { analytics: parsed.analytics ?? null, v: parsed.v };
  } catch {
    return { analytics: null, v: null };
  }
}

function writeConsent(analytics) {
  localStorage.setItem(
    CONSENT_KEY,
    JSON.stringify({ v: CONSENT_VERSION, analytics, at: new Date().toISOString() }),
  );
}

export function grantAnalyticsConsent() {
  writeConsent("granted");
  initPostHogIfConsented();
}

export function denyAnalyticsConsent() {
  writeConsent("denied");
  // If PostHog was previously loaded in this tab (before the user opened
  // the banner via the footer link), stop it and clear its persistent
  // identifiers so nothing else leaks server-side.
  try {
    if (window.posthog?.opt_out_capturing) window.posthog.opt_out_capturing();
    if (window.posthog?.stopSessionRecording) window.posthog.stopSessionRecording();
    if (window.posthog?.reset) window.posthog.reset();
  } catch {
    /* noop */
  }
}

export function resetConsent() {
  localStorage.removeItem(CONSENT_KEY);
}

let _posthogLoading = false;

export function initPostHogIfConsented() {
  if (readConsent().analytics !== "granted") return;
  if (typeof window === "undefined") return;
  if (window.__posthog_loaded__) return;
  if (_posthogLoading) return;
  _posthogLoading = true;

  // Minimal PostHog loader — same array-shim technique as the official
  // snippet, kept small on purpose. Only fires after this call.
  (function (t, e) {
    // eslint-disable-next-line no-unused-vars, prefer-const
    let o, n, p, r;
    if (e.__SV) return;
    window.posthog = e;
    e._i = [];
    e.init = function (i, s, a) {
      function g(t, e) {
        const parts = e.split(".");
        let target = t;
        let name = e;
        if (parts.length === 2) {
          target = t[parts[0]];
          name = parts[1];
        }
        target[name] = function () {
          target.push([name].concat(Array.prototype.slice.call(arguments, 0)));
        };
      }
      p = t.createElement("script");
      p.type = "text/javascript";
      p.crossOrigin = "anonymous";
      p.async = true;
      p.src = s.api_host.replace(".i.posthog.com", "-assets.i.posthog.com") + "/static/array.js";
      r = t.getElementsByTagName("script")[0];
      r.parentNode.insertBefore(p, r);
      let u = e;
      if (typeof a !== "undefined") u = e[a] = [];
      else a = "posthog";
      u.people = u.people || [];
      const methods = "init capture identify reset opt_in_capturing opt_out_capturing has_opted_in_capturing startSessionRecording stopSessionRecording set_config debug".split(
        " ",
      );
      for (let n = 0; n < methods.length; n++) g(u, methods[n]);
      e._i.push([i, s, a]);
    };
    e.__SV = 1;
  })(document, window.posthog || []);

  window.posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: "identified_only",
    session_recording: {
      recordCrossOriginIframes: true,
      capturePerformance: false,
    },
    // Explicit — the loader would default to true; be defensive.
    opt_out_capturing_by_default: false,
    disable_session_recording: false,
  });
  window.__posthog_loaded__ = true;
}
